/**
 * ReportTable Component - Phase 5 Implementation
 * Data table component for displaying report data in tabular format
 * Includes sorting, pagination, and responsive design
 */

import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { 
  ArrowUpDown, 
  ArrowUp, 
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { ReportType } from '../../types/reports';

interface ReportTableProps {
  reportType: ReportType;
  data: any[];
  isLoading?: boolean;
  error?: string;
  className?: string;
}

interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatPercentage = (value: number): string => {
  return `${value.toFixed(1)}%`;
};

const formatDate = (value: string | Date): string => {
  const date = new Date(value);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const getColumnsForReportType = (reportType: ReportType) => {
  const baseColumns = {
    'sales-dashboard': [
      { key: 'date', title: 'Date', type: 'date', sortable: true },
      { key: 'revenue', title: 'Revenue', type: 'currency', sortable: true },
      { key: 'orders', title: 'Orders', type: 'number', sortable: true },
      { key: 'customers', title: 'Customers', type: 'number', sortable: true },
      { key: 'growth', title: 'Growth', type: 'percentage', sortable: true },
    ],
    'cash-flow-statement': [
      { key: 'date', title: 'Date', type: 'date', sortable: true },
      { key: 'inflow', title: 'Cash Inflow', type: 'currency', sortable: true },
      { key: 'outflow', title: 'Cash Outflow', type: 'currency', sortable: true },
      { key: 'netFlow', title: 'Net Flow', type: 'currency', sortable: true },
      { key: 'balance', title: 'Balance', type: 'currency', sortable: true },
    ],
    'profit-loss': [
      { key: 'category', title: 'Category', type: 'string', sortable: true },
      { key: 'revenue', title: 'Revenue', type: 'currency', sortable: true },
      { key: 'expenses', title: 'Expenses', type: 'currency', sortable: true },
      { key: 'netIncome', title: 'Net Income', type: 'currency', sortable: true },
      { key: 'margin', title: 'Margin', type: 'percentage', sortable: true },
    ],
    'balance-sheet': [
      { key: 'account', title: 'Account', type: 'string', sortable: true },
      { key: 'assets', title: 'Assets', type: 'currency', sortable: true },
      { key: 'liabilities', title: 'Liabilities', type: 'currency', sortable: true },
      { key: 'equity', title: 'Equity', type: 'currency', sortable: true },
      { key: 'ratio', title: 'Ratio', type: 'percentage', sortable: true },
    ],
    'inventory-status': [
      { key: 'item', title: 'Item', type: 'string', sortable: true },
      { key: 'category', title: 'Category', type: 'string', sortable: true },
      { key: 'quantity', title: 'Quantity', type: 'number', sortable: true },
      { key: 'value', title: 'Value', type: 'currency', sortable: true },
      { key: 'status', title: 'Status', type: 'badge', sortable: true },
    ],
    'customer-outstanding': [
      { key: 'customer', title: 'Customer', type: 'string', sortable: true },
      { key: 'amount', title: 'Amount', type: 'currency', sortable: true },
      { key: 'daysOutstanding', title: 'Days Outstanding', type: 'number', sortable: true },
      { key: 'dueDate', title: 'Due Date', type: 'date', sortable: true },
      { key: 'status', title: 'Status', type: 'badge', sortable: true },
    ],
    'vendor-payments': [
      { key: 'vendor', title: 'Vendor', type: 'string', sortable: true },
      { key: 'amount', title: 'Amount', type: 'currency', sortable: true },
      { key: 'paymentDate', title: 'Payment Date', type: 'date', sortable: true },
      { key: 'method', title: 'Method', type: 'string', sortable: true },
      { key: 'status', title: 'Status', type: 'badge', sortable: true },
    ],
  };

  return baseColumns[reportType] || [];
};

const ReportTable: React.FC<ReportTableProps> = ({
  reportType,
  data,
  isLoading = false,
  error,
  className
}) => {
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  const columns = getColumnsForReportType(reportType);

  const sortedData = useMemo(() => {
    if (!sortConfig || !data.length) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      let comparison = 0;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue);
      } else {
        comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      }

      return sortConfig.direction === 'desc' ? comparison * -1 : comparison;
    });
  }, [data, sortConfig]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return sortedData.slice(startIndex, startIndex + pageSize);
  }, [sortedData, currentPage, pageSize]);

  const totalPages = Math.ceil(sortedData.length / pageSize);

  const handleSort = (key: string) => {
    setSortConfig(current => {
      if (!current || current.key !== key) {
        return { key, direction: 'asc' };
      }
      if (current.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      return null;
    });
  };

  const renderSortIcon = (key: string) => {
    if (!sortConfig || sortConfig.key !== key) {
      return <ArrowUpDown className="h-4 w-4 text-muted-foreground" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4" />
      : <ArrowDown className="h-4 w-4" />;
  };

  const renderCellValue = (value: any, type: string) => {
    if (value === null || value === undefined) {
      return <span className="text-muted-foreground">—</span>;
    }

    switch (type) {
      case 'currency':
        return <span className="font-mono">{formatCurrency(value)}</span>;
      case 'percentage':
        return <span className="font-mono">{formatPercentage(value)}</span>;
      case 'date':
        return formatDate(value);
      case 'number':
        return <span className="font-mono">{value.toLocaleString()}</span>;
      case 'badge':
        const variant = value === 'overdue' || value === 'low' ? 'destructive' :
                      value === 'pending' || value === 'medium' ? 'secondary' :
                      'default';
        return <Badge variant={variant}>{value}</Badge>;
      default:
        return value;
    }
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center space-y-2">
          <p className="text-destructive font-medium">Failed to load table data</p>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex space-x-4">
          {columns.slice(0, 5).map((_, i) => (
            <Skeleton key={i} className="h-8 flex-1" />
          ))}
        </div>
        {Array.from({ length: pageSize }).map((_, i) => (
          <div key={i} className="flex space-x-4">
            {columns.slice(0, 5).map((_, j) => (
              <Skeleton key={j} className="h-12 flex-1" />
            ))}
          </div>
        ))}
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center space-y-2">
          <p className="text-muted-foreground">No data available</p>
          <p className="text-sm text-muted-foreground">
            Try adjusting the date range or filters
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("space-y-4", className)}>
      <div className="rounded-md border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead 
                  key={column.key}
                  className={cn(
                    column.sortable && "cursor-pointer select-none hover:bg-muted/50",
                    "font-medium"
                  )}
                  onClick={column.sortable ? () => handleSort(column.key) : undefined}
                >
                  <div className="flex items-center space-x-2">
                    <span>{column.title}</span>
                    {column.sortable && renderSortIcon(column.key)}
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedData.map((row, index) => (
              <TableRow key={index}>
                {columns.map((column) => (
                  <TableCell key={column.key}>
                    {renderCellValue(row[column.key], column.type)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {((currentPage - 1) * pageSize) + 1} to{' '}
            {Math.min(currentPage * pageSize, sortedData.length)} of{' '}
            {sortedData.length} entries
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(current => Math.max(1, current - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            
            <div className="flex items-center space-x-1">
              <span className="text-sm">Page</span>
              <span className="text-sm font-medium">{currentPage}</span>
              <span className="text-sm">of</span>
              <span className="text-sm font-medium">{totalPages}</span>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(current => Math.min(totalPages, current + 1))}
              disabled={currentPage === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportTable;